import { useId, useState } from 'react';
import {
  Button,
  ChipToggle,
  Dialog,
  Field,
  Input,
  useToast,
} from '@/components/ui';
import { formatRM, parseRM, senToInput } from '@/features/billing/money';
import { useBranch } from '@/features/branch/branch-context';
import {
  useLabPackageActions,
  useLabPackages,
} from '@/features/lab-packages/api';
import {
  LAB_PACKAGE_LIMITS as L,
  type LabPackage,
  type LabPackageInput,
} from '@/features/lab-packages/types';
import { useLabCatalogue } from '@/features/orders/api';
import { isApiError } from '@/lib/api';
import styles from '@/pages/queue/queue.module.css';
import { LabFailureBox } from './LabFailureBox';
import {
  intIn,
  RELOAD_CODES,
  toFailure,
  type Failure,
} from './lab-package-format';
import own from './LabPackages.module.css';

interface Values {
  name: string;
  nameMs: string;
  summary: string;
  summaryMs: string;
  tests: string[];
  price: string;
  member: string;
  validity: string;
  fasting: string;
  sortOrder: string;
  branchIds: string[];
  homeVisitAllowed: boolean;
  panelCovered: boolean;
  sellInApp: boolean;
}

type Errors = Partial<Record<keyof Values, string>>;

function valuesOf(p: LabPackage | null): Values {
  return {
    name: p?.name ?? '',
    nameMs: p?.nameMs ?? '',
    summary: p?.summary ?? '',
    summaryMs: p?.summaryMs ?? '',
    tests: p?.tests ?? [],
    price: p ? senToInput(p.priceSen) : '',
    member: p?.memberPriceSen != null ? senToInput(p.memberPriceSen) : '',
    validity: p ? String(p.validityDays) : '30',
    fasting: p?.fastingHours != null ? String(p.fastingHours) : '',
    sortOrder: p ? String(p.sortOrder) : '0',
    branchIds: p?.branchIds ?? [],
    homeVisitAllowed: p?.homeVisitAllowed ?? true,
    panelCovered: p?.panelCovered ?? false,
    sellInApp: p?.sellInApp ?? false,
  };
}

const opt = (s: string) => (s.trim() ? s.trim() : null);

/** Client checks mirroring CreateLabPackageDto; returns the body or errors. */
function check(
  v: Values,
): { body: LabPackageInput; errors?: undefined } | { errors: Errors } {
  const e: Errors = {};
  const name = v.name.trim();
  if (name.length < L.nameMin)
    e.name = 'Enter the package name (at least 2 characters).';
  if (!v.tests.length) e.tests = 'Choose at least one test.';
  else if (v.tests.length > L.testsMax)
    e.tests = `Choose at most ${L.testsMax} tests.`;
  const priceSen = parseRM(v.price);
  if (priceSen === null || priceSen > L.priceMaxSen)
    e.price = 'Enter the price in RM, e.g. 95.00.';
  const memberSen = v.member.trim() ? parseRM(v.member) : null;
  if (v.member.trim() && (memberSen === null || memberSen > L.priceMaxSen))
    e.member = 'Enter the member price in RM, or leave it empty.';
  else if (memberSen !== null && priceSen !== null && memberSen > priceSen)
    e.member = 'The member price cannot be above the price.';
  const validityDays = intIn(v.validity, L.validityMin, L.validityMax);
  if (validityDays === null)
    e.validity = `Enter ${L.validityMin}–${L.validityMax} days.`;
  const fastingHours = v.fasting.trim()
    ? intIn(v.fasting, L.fastingMin, L.fastingMax)
    : null;
  if (v.fasting.trim() && fastingHours === null)
    e.fasting = `Enter ${L.fastingMin}–${L.fastingMax} hours, or leave it empty for no fasting.`;
  const sortOrder = intIn(v.sortOrder || '0', 0, L.sortMax);
  if (sortOrder === null) e.sortOrder = `Enter 0–${L.sortMax}.`;
  if (Object.keys(e).length) return { errors: e };
  return {
    body: {
      name,
      nameMs: opt(v.nameMs),
      summary: opt(v.summary),
      summaryMs: opt(v.summaryMs),
      tests: v.tests,
      priceSen: priceSen!,
      memberPriceSen: memberSen,
      validityDays: validityDays!,
      fastingHours,
      sortOrder: sortOrder!,
      branchIds: v.branchIds,
      homeVisitAllowed: v.homeVisitAllowed,
      panelCovered: v.panelCovered,
      sellInApp: v.sellInApp,
    },
  };
}

/**
 * "Add package" / "Edit package" (RL-1138): names and one-line summaries in
 * English and Malay, the catalogue tests, price and member price, validity,
 * fasting, home visit, branches and whether it is sold in the app. Edits send
 * the package's `version`; "Switch off" keeps it for existing bookings.
 */
export function LabPackageDialog({
  pkg,
  onClose,
}: {
  pkg: LabPackage | null;
  onClose: () => void;
}) {
  const formId = useId();
  const toast = useToast();
  const { branches } = useBranch();
  const act = useLabPackageActions();
  const catalogue = useLabCatalogue();
  // The latest copy of the package (reloaded after a 409), for its version.
  const latestList = useLabPackages(true);
  const current = pkg
    ? (latestList.data?.find((p) => p.id === pkg.id) ?? pkg)
    : null;
  const [values, setValues] = useState<Values>(() => valuesOf(pkg));
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<Failure | null>(null);
  const [confirmSwitch, setConfirmSwitch] = useState(false);
  const busy = act.create.isPending || act.update.isPending;

  const set = <K extends keyof Values>(k: K, value: Values[K]) => {
    setValues((v) => ({ ...v, [k]: value }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  function fail(err: unknown) {
    setFailure(toFailure(err));
    if (isApiError(err) && RELOAD_CODES.has(err.code))
      void latestList.refetch();
  }

  async function save() {
    setFailure(null);
    const r = check(values);
    if (r.errors) return setErrors(r.errors);
    try {
      const saved = current
        ? await act.update.mutateAsync({
            id: current.id,
            body: { ...r.body, version: current.version },
          })
        : await act.create.mutateAsync(r.body);
      toast.show({
        tone: 'success',
        title: current
          ? `${saved.name} saved: new bookings use these details`
          : `${saved.name} added${saved.sellInApp ? '' : ': switch on “Sold in the app” when it is ready'}`,
      });
      onClose();
    } catch (err) {
      fail(err);
    }
  }

  async function switchActive() {
    if (!current) return;
    setFailure(null);
    try {
      const saved = await act.update.mutateAsync({
        id: current.id,
        body: { version: current.version, isActive: !current.isActive },
      });
      toast.show({
        tone: 'success',
        title: saved.isActive
          ? `${saved.name} switched on`
          : `${saved.name} switched off: no new bookings`,
      });
      onClose();
    } catch (err) {
      setConfirmSwitch(false);
      fail(err);
    }
  }

  const testOptions = [
    ...(catalogue.data ?? [])
      .filter((t) => t.isActive)
      .map((t) => ({ value: t.name, label: t.name })),
    // Keep tests already on the package selectable.
    ...values.tests
      .filter((n) => !catalogue.data?.some((t) => t.name === n && t.isActive))
      .map((n) => ({ value: n, label: n })),
  ];
  const changedElsewhere =
    !!pkg && !!current && current.version !== pkg.version;

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={pkg ? `Edit ${pkg.name}` : 'Add package'}
      footer={
        <>
          {current && (
            <Button
              variant={current.isActive ? 'danger' : 'secondary'}
              loading={busy && confirmSwitch}
              onClick={() =>
                confirmSwitch ? void switchActive() : setConfirmSwitch(true)
              }
            >
              {confirmSwitch
                ? current.isActive
                  ? 'Confirm switch off'
                  : 'Confirm switch on'
                : current.isActive
                  ? 'Switch off'
                  : 'Switch on'}
            </Button>
          )}
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={busy && !confirmSwitch}
          >
            {pkg ? 'Save package' : 'Add package'}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className={styles.drawer}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {failure && <LabFailureBox failure={failure} />}
        {confirmSwitch && current && (
          <p className={styles.warnNote} role="status">
            {current.isActive
              ? 'Switched off, the package is no longer offered in the app. Bookings already made keep it.'
              : 'Switched on, the package is offered again where “Sold in the app” is on.'}
          </p>
        )}
        {changedElsewhere && (
          <p className={own.note} role="status">
            Someone else changed this package since you opened it. Your changes
            are kept; saving replaces theirs.{' '}
            <Button
              size="sm"
              onClick={() => {
                setValues(valuesOf(current));
                setErrors({});
                setFailure(null);
              }}
            >
              Load their latest details
            </Button>
          </p>
        )}
        {pkg && (
          <p className={own.note}>
            Price changes apply to new bookings only; booked tests keep their
            bill lines.
          </p>
        )}
        <div className={own.grid}>
          <Field label="Name (English)" error={errors.name}>
            <Input
              value={values.name}
              maxLength={L.nameMax}
              onChange={(e) => set('name', e.target.value)}
            />
          </Field>
          <Field
            label="Name (Bahasa Melayu)"
            hint="Empty = the app shows the English name."
          >
            <Input
              lang="ms"
              value={values.nameMs}
              maxLength={L.nameMax}
              onChange={(e) => set('nameMs', e.target.value)}
            />
          </Field>
          <Field label="What is included (English)" hint="One line.">
            <Input
              value={values.summary}
              maxLength={L.summaryMax}
              onChange={(e) => set('summary', e.target.value)}
            />
          </Field>
          <Field label="What is included (Bahasa Melayu)">
            <Input
              lang="ms"
              value={values.summaryMs}
              maxLength={L.summaryMax}
              onChange={(e) => set('summaryMs', e.target.value)}
            />
          </Field>
        </div>
        <fieldset className={own.checks}>
          <legend className={own.legend}>
            Tests ({values.tests.length} chosen)
          </legend>
          <div className={own.tests}>
            {catalogue.isLoading ? (
              <p className={own.note}>Loading the lab catalogue…</p>
            ) : catalogue.isError ? (
              <p className={own.note}>Could not load the lab catalogue.</p>
            ) : (
              <ChipToggle
                label="Tests in the package"
                options={testOptions}
                selected={values.tests}
                onChange={(next) => set('tests', next)}
              />
            )}
          </div>
          {errors.tests && (
            <p className={own.fieldError} role="alert">
              {errors.tests}
            </p>
          )}
        </fieldset>
        <div className={own.grid}>
          <Field
            label="Price (RM)"
            error={errors.price}
            hint={
              parseRM(values.price) !== null
                ? formatRM(parseRM(values.price)!)
                : undefined
            }
          >
            <Input
              inputMode="decimal"
              value={values.price}
              onChange={(e) => set('price', e.target.value)}
            />
          </Field>
          <Field
            label="Member price (RM)"
            hint="Verified members only; never a panel-covered package. Empty = none."
            error={errors.member}
          >
            <Input
              inputMode="decimal"
              value={values.member}
              onChange={(e) => set('member', e.target.value)}
            />
          </Field>
          <Field
            label="Valid for (days)"
            hint="The sample must be taken within this many days of booking."
            error={errors.validity}
          >
            <Input
              inputMode="numeric"
              value={values.validity}
              onChange={(e) => set('validity', e.target.value)}
            />
          </Field>
          <Field
            label="Fasting (hours)"
            hint="Hours without food before the sample. Empty = no fasting."
            error={errors.fasting}
          >
            <Input
              inputMode="numeric"
              value={values.fasting}
              onChange={(e) => set('fasting', e.target.value)}
            />
          </Field>
          <Field
            label="Order in the app"
            hint="Lower numbers show first."
            error={errors.sortOrder}
          >
            <Input
              inputMode="numeric"
              value={values.sortOrder}
              onChange={(e) => set('sortOrder', e.target.value)}
            />
          </Field>
        </div>
        <ChipToggle
          label="Branches selling it (none chosen = all)"
          options={branches.map((b) => ({ value: b.id, label: b.name }))}
          selected={values.branchIds}
          onChange={(next) => set('branchIds', next)}
        />
        <fieldset className={own.checks}>
          <legend className={own.legend}>Options</legend>
          <label className={own.check}>
            <input
              type="checkbox"
              checked={values.homeVisitAllowed}
              onChange={(e) => set('homeVisitAllowed', e.target.checked)}
            />
            The patient may choose a home visit (where home visits are offered)
          </label>
          <label className={own.check}>
            <input
              type="checkbox"
              checked={values.panelCovered}
              onChange={(e) => set('panelCovered', e.target.checked)}
            />
            A panel the patient names may cover it (otherwise the patient pays)
          </label>
          <label className={own.check}>
            <input
              type="checkbox"
              checked={values.sellInApp}
              onChange={(e) => set('sellInApp', e.target.checked)}
            />
            Sold in the app (shown and bookable)
          </label>
        </fieldset>
      </form>
    </Dialog>
  );
}
